import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import logo from '../Components/Assets/logo.png';
import API_BASE_URL from '../config';
import { 
  FaExclamationCircle, 
  FaEnvelope, 
  FaLock, 
  FaUser, 
  FaCheckCircle,
  FaEye,
  FaEyeSlash,
  FaGoogle,
  FaFacebook
} from 'react-icons/fa';
import { MdOutlinePrivacyTip } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import './CSS/LoginSignup.css';

const LoginSignup = () => {
  const [state, setState] = useState("Login");
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('auth-token');
    if (token) {
      setIsAuthenticated(true);
      // Redirect to home/dashboard if already logged in
      navigate('/');
    }
  }, [navigate]);

  // If already authenticated, don't show login page
  if (isAuthenticated) {
    return (
      <div className='already-logged-in'>
        <div className="logged-in-container">
          <FaCheckCircle className="success-icon" />
          <h2>You are already logged in!</h2>
          <p>Redirecting to your dashboard...</p>
          <button onClick={() => navigate('/')} className="go-to-dashboard">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const changeHandler = (e) => {
    setFormData({...formData, [e.target.name]: e.target.value});
  }

  const validateForm = () => {
    if (!formData.email.trim()) {
      toast.error("Email is required", { icon: <FaExclamationCircle /> });
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address", { icon: <FaExclamationCircle /> });
      return false;
    }
    
    if (!formData.password.trim()) {
      toast.error("Password is required", { icon: <FaExclamationCircle /> });
      return false;
    }
    
    if (state === "Sign Up" && !formData.username.trim()) {
      toast.error("Username is required", { icon: <FaExclamationCircle /> });
      return false;
    }
    
    return true;
  };

  const login = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    let responseData;
    
    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      responseData = await response.json();
      
      if (responseData.success) {
        localStorage.setItem('auth-token', responseData.token);
        localStorage.setItem('user-name', formData.username || formData.email.split('@')[0]);
        localStorage.setItem('user-email', formData.email);
        
        // Update authentication state
        setIsAuthenticated(true);
        
        // Success toast with user welcome
        toast.success(
          (t) => (
            <div className="login-success-toast">
              <div className="toast-header">
                <FaCheckCircle className="toast-icon" />
                <span>Welcome Back! 👋</span>
              </div>
              <p>You have successfully logged in.</p>
            </div>
          ),
          {
            duration: 3000,
            position: 'top-center',
            style: {
              background: '#ff4141',
              color: 'white',
              borderRadius: '10px'
            }
          }
        );
        
        // Short delay before redirecting
        setTimeout(() => {
          navigate('/');
          window.location.reload(); // Refresh to update navbar
        }, 1000);
        
      } else {
        toast.error(responseData.error || "Login failed", {
          icon: <FaExclamationCircle />,
        });
      }
    } catch (error) {
      toast.error("Network error. Please try again.", {
        icon: <FaExclamationCircle />,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    let responseData;
    
    try {
      const response = await fetch(`${API_BASE_URL}/signup`, {
        method: "POST",
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      responseData = await response.json();
      
      if (responseData.success) {
        localStorage.setItem('auth-token', responseData.token);
        localStorage.setItem('user-name', formData.username);
        localStorage.setItem('user-email', formData.email);
        
        // Update authentication state
        setIsAuthenticated(true);
        
        // Welcome toast for new users
        toast.success(
          (t) => (
            <div className="signup-success-toast">
              <div className="toast-header">
                <FaCheckCircle className="toast-icon" />
                <span>Welcome to NepliShop! 🎉</span>
              </div>
              <p>Your account has been created successfully!</p>
              <p className="toast-subtext">Check your email for a 15% welcome discount.</p>
            </div>
          ),
          {
            duration: 4000,
            position: 'top-center',
            style: {
              background: '#ff4141',
              color: 'white',
              borderRadius: '10px'
            }
          }
        );
        
        setTimeout(() => {
          navigate('/');
          window.location.reload(); // Refresh to update navbar
        }, 1500);
        
      } else {
        toast.error(responseData.error || "Signup failed", {
          icon: <FaExclamationCircle />,
        });
      }
    } catch (error) {
      toast.error("Network error. Please try again.", {
        icon: <FaExclamationCircle />,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    toast.loading(`Redirecting to ${provider} login...`, {
      duration: 2000,
    });
    // In a real app, you would redirect to OAuth endpoint
    // window.location.href = `${API_BASE_URL}/auth/${provider}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    state === "Login" ? login() : signup();
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleState = () => {
    setState(state === "Login" ? "Sign Up" : "Login");
    // Clear password field when switching
    setFormData({...formData, password: ""});
  };

  return (
    <div className='loginsignup'>
      <div className="loginsignup-background">
        <div className="background-pattern"></div>
        <div className="background-overlay"></div>
      </div>
      
      <div className="loginsignup-container">
        <div className="loginsignup-header">
          <div className="logo-container">
            <div className="logo-circle">
              <img src={logo} alt="NepliShop Logo" className="logo-icon" loading="eager" decoding="async" fetchpriority="high" width="64" height="64" />
             
            </div>
            <h1>NEPLISHOP</h1>
          </div>
          <h2>{state === "Login" ? "Welcome Back!" : "Join Our Fashion Family"}</h2>
          <p className="subtitle">
            {state === "Login" 
              ? "Sign in to your account to continue shopping" 
              : "Create an account to get exclusive offers and updates"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="loginsignup-form">
          <div className="form-content">
            {state === "Sign Up" && (
              <div className="input-group">
                <div className="input-icon">
                  <FaUser />
                </div>
                <input 
                  name='username' 
                  value={formData.username} 
                  onChange={changeHandler} 
                  type="text" 
                  placeholder='Full Name' 
                  className="styled-input"
                  required
                />
                <label className="input-label">Full Name</label>
              </div>
            )}

            <div className="input-group">
              <div className="input-icon">
                <FaEnvelope />
              </div>
              <input 
                name='email' 
                value={formData.email} 
                onChange={changeHandler} 
                type="email" 
                placeholder='Email Address' 
                className="styled-input"
                required
              />
              <label className="input-label">Email Address</label>
            </div>

            <div className="input-group">
              <div className="input-icon">
                <FaLock />
              </div>
              <input 
                name='password' 
                value={formData.password} 
                onChange={changeHandler} 
                type={showPassword ? "text" : "password"} 
                placeholder='Password' 
                className="styled-input"
                required
                minLength="6"
              />
              <label className="input-label">Password (min. 6 characters)</label>
              <button 
                type="button" 
                className="password-toggle"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {state === "Login" && (
              <div className="form-options">
                <div className="remember-me">
                  <input type="checkbox" id="remember" />
                  <label htmlFor="remember">Remember me</label>
                </div>
                <a href="/forgot-password" className="forgot-password">
                  Forgot Password?
                </a>
              </div>
            )}

            <div className="terms-agreement">
              <div className="checkbox-container">
                <input 
                  type="checkbox" 
                  id="terms" 
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  required
                />
                <div className="custom-checkbox">
                  {agreeToTerms && <FaCheckCircle />}
                </div>
              </div>
              <label htmlFor="terms">
                I agree to the <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>
                <MdOutlinePrivacyTip className="privacy-icon" />
              </label>
            </div>

            <button 
              type="submit" 
              className="submit-btn"
              disabled={isLoading || !agreeToTerms}
            >
              {isLoading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                <>
                  {state === "Login" ? "Sign In" : "Create Account"}
                  <div className="btn-arrow">→</div>
                </>
              )}
            </button>

            <div className="divider">
              <span>Or continue with</span>
            </div>

            <div className="social-login">
              <button 
                type="button" 
                className="social-btn google"
                onClick={() => handleSocialLogin('google')}
              >
                <FaGoogle />
                <span>Google</span>
              </button>
              <button 
                type="button" 
                className="social-btn facebook"
                onClick={() => handleSocialLogin('facebook')}
              >
                <FaFacebook />
                <span>Facebook</span>
              </button>
            </div>

            <div className="state-toggle">
              <p>
                {state === "Login" 
                  ? "Don't have an account?" 
                  : "Already have an account?"}
                <button type="button" className="toggle-link" onClick={toggleState}>
                  {state === "Login" ? " Sign Up" : " Sign In"}
                </button>
              </p>
            </div>
          </div>
        </form>

        <div className="login-benefits">
          <h3>Benefits of joining:</h3>
          <ul>
            <li><FaCheckCircle /> Exclusive member-only discounts</li>
            <li><FaCheckCircle /> Early access to new collections</li>
            <li><FaCheckCircle /> Personalized style recommendations</li>
            <li><FaCheckCircle /> Faster checkout process</li>
            <li><FaCheckCircle /> Order tracking & history</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LoginSignup;

